import { useEffect, useState } from 'react'
import {
  useAckUnlocks,
  useEngagementEnabled,
  useEngagementMe,
} from '@/hooks/queries/useEngagement'
import { queryKeys } from '@/hooks/query-keys'
import { registerEngagementActivityListener } from '@/lib/engagement/activity-signal'
import { useEntityRealtime } from '@/lib/realtime'
import { useOrganizationStore } from '@/store/organization.store'
import { getOrgPath } from '@/utils/org-paths'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { EngagementUnlock, Notification } from '@scrambl/shared'
import { NotificationType } from '@scrambl/shared'

import {
  nextUnlock,
  presentUnlock,
  previewUnlockFromParam,
} from './unlock-copy'
import { UnlockMoment } from './UnlockMoment'

const UNLOCK_NOTIFICATION_TYPES = new Set<string>([
  NotificationType.ACHIEVEMENT_UNLOCKED,
  NotificationType.GOAL_ACHIEVED,
])

/**
 * Mounted once beside the app's Toaster. Renders nothing unless engagement is
 * enabled for this work account, so a workspace without the flag pays nothing.
 */
export function UnlockMomentHost() {
  const enabled = useEngagementEnabled()
  if (!enabled) return null
  return <UnlockMomentController />
}

function UnlockMomentController() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const organization = useOrganizationStore(state => state.currentOrganization)
  const { data: me } = useEngagementMe()
  const ackUnlocks = useAckUnlocks()
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set())
  const [previews, setPreviews] = useState<EngagementUnlock[]>([])

  // Primary refresh: the API layer notes unlock-capable writes.
  useEffect(
    () =>
      registerEngagementActivityListener(() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.engagement.me(),
        })
      }),
    [queryClient],
  )

  // Fallback refresh: unlocks found by the server out of band (nightly sweep,
  // another device) arrive as notifications.
  const { addEventListener, isConnected } = useEntityRealtime(
    'organization',
    organization?.id ?? null,
    { suppressLogging: true },
  )
  useEffect(() => {
    if (!organization?.id || !isConnected) return
    return addEventListener(
      'NOTIFICATION_CREATED',
      (data: { notification?: Notification }) => {
        if (!UNLOCK_NOTIFICATION_TYPES.has(String(data.notification?.type)))
          return
        void queryClient.invalidateQueries({
          queryKey: queryKeys.engagement.all,
        })
      },
      {
        resolveEntityId: data => {
          const typed = data as {
            organizationId?: string
            notification?: { organizationId?: string }
          }
          return (
            typed.organizationId ??
            typed.notification?.organizationId ??
            organization.id
          )
        },
      },
    )
  }, [addEventListener, isConnected, organization?.id, queryClient])

  // Dev-only: `?preview-unlock=streak_7` plays a moment without earning it.
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const value = new URLSearchParams(location.search).get('preview-unlock')
    setPreviews(
      (value ?? '')
        .split(',')
        .map(part => previewUnlockFromParam(part.trim()))
        .filter((unlock): unlock is EngagementUnlock => unlock !== null),
    )
  }, [location.search])

  const level = me?.preferences.celebrationLevel ?? 'full'
  const unseen = me?.unseen ?? []
  const pending = unseen.filter(unlock => !dismissed.has(unlock.id))

  // Quiet and off levels never take over the screen.
  useEffect(() => {
    if (level === 'full' || pending.length === 0) return
    if (level === 'quiet') {
      for (const unlock of pending) {
        const presentation = presentUnlock(unlock)
        if (!presentation) continue
        toast(presentation.title, {
          description: presentation.line,
          action: organization?.slug
            ? {
                label: 'View',
                onClick: () =>
                  navigate(getOrgPath(organization.slug, '/achievements')),
              }
            : undefined,
        })
      }
    }
    setDismissed(current => {
      const next = new Set(current)
      for (const unlock of pending) next.add(unlock.id)
      return next
    })
    ackUnlocks.mutate(pending.map(({ id, kind }) => ({ id, kind })))
  }, [level, pending, organization?.slug, navigate, ackUnlocks])

  // Dev previews first, then real unlocks (full celebration level only).
  const queue = [
    ...previews.filter(unlock => !dismissed.has(unlock.id)),
    ...(level === 'full'
      ? unseen.filter(unlock => !dismissed.has(unlock.id))
      : []),
  ]
  const activeUnlock =
    queue.find(unlock => unlock.id.startsWith('preview:')) ??
    nextUnlock(queue, dismissed)
  const presentation = activeUnlock ? presentUnlock(activeUnlock) : null

  // A key this build can't draw (catalog drift): acknowledge it silently.
  useEffect(() => {
    if (!activeUnlock || presentation) return
    setDismissed(current => new Set(current).add(activeUnlock.id))
    if (!activeUnlock.id.startsWith('preview:'))
      ackUnlocks.mutate([{ id: activeUnlock.id, kind: activeUnlock.kind }])
  }, [activeUnlock, presentation, ackUnlocks])

  const dismiss = () => {
    if (!activeUnlock) return
    setDismissed(current => new Set(current).add(activeUnlock.id))
    if (!activeUnlock.id.startsWith('preview:'))
      ackUnlocks.mutate([{ id: activeUnlock.id, kind: activeUnlock.kind }])
  }

  const viewAll = () => {
    dismiss()
    if (organization?.slug)
      navigate(getOrgPath(organization.slug, '/achievements'))
  }

  return (
    <AnimatePresence mode="wait">
      {presentation && (
        <UnlockMoment
          key={presentation.id}
          unlock={presentation}
          remaining={Math.max(0, queue.length - 1)}
          onDismiss={dismiss}
          onViewAll={viewAll}
        />
      )}
    </AnimatePresence>
  )
}
